/*  MUI  */
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Typography
} from '@mui/material';
import { FontSizes } from 'application/Shared/Constants';

/* Shared */
import {
    Modal,
    ModalBody,
    ModalFooter
} from 'application/Shared/Modals/Components';
import { useFetchApplicationByParams } from 'hooks/dataAccess/Application/application';
import {
    useActivateRecipeAsync,
    useFetchCustomerConnections,
    useOnConnectionSuccessAsync
} from 'hooks/dataAccess/Workato/workato';

/* React */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { WorkatoIFrame } from './WorkatoIFrame';

const defaultOnSuccess = () => {};

const ConnectionError = () => {
    const { t } = useTranslation();
    return (
        <>
            <Alert
                severity="error"
                icon={false}
                style={{
                    flexDirection: 'column',
                    marginBottom: '32px'
                }}
            >
                <Typography
                    sx={{ alignSelf: 'center' }}
                    component="p"
                    dangerouslySetInnerHTML={{
                        __html: t('SaasOps.integration.error.issue')
                    }}
                />
            </Alert>
            <Typography sx={{ fontSize: FontSizes[2] }} variant="body2">
                <Trans i18nKey="SaasOps.integration.error.message" />
            </Typography>
        </>
    );
};

export const WorkatoConnectionsModal = ({
    displayName,
    workatoProvider,
    setOpenModal,
    onSuccessFn,
    clientId,
    onClose = () => {}
}) => {
    const { t } = useTranslation();
    const initialLoad = useRef(true);
    const [recipeError, setRecipeError] = useState(null);
    const [activatingRecipe, setActivatingRecipe] = useState(false);

    const params = useMemo(
        () => ({
            workatoProvider
        }),
        [workatoProvider]
    );

    const onSuccess = onSuccessFn || defaultOnSuccess;

    const { activateRecipe } = useActivateRecipeAsync();
    const { onConnectionSuccess } = useOnConnectionSuccessAsync();

    const handleMessageFn = async (message) => {
        if (message?.type === 'connectionStatusChange') {
            // This block is executed on initial load if the connection is already connected
            if (message?.payload?.connected && !initialLoad.current) {
                setActivatingRecipe(true);
                const res = await activateRecipe(clientId, workatoProvider);
                setActivatingRecipe(false);
                const { success, error } = res;
                // Activate recipes and if it is successful and a subsequent call, run onSuccess
                if (success) {
                    onConnectionSuccess(clientId, workatoProvider);
                    onSuccess();
                    initialLoad.current = true;
                }
                if (error) {
                    setRecipeError(true);
                }
            }
            if (initialLoad.current) initialLoad.current = false;
        }
    };

    const { application, loading: loadingApplication } =
        useFetchApplicationByParams(params);

    const fetchCustomerconnectionsParams = application.id
        ? {
              applicationId: application.id,
              platformTenantId: clientId
          }
        : {};
    const { connections, loading: loadingConnections } =
        useFetchCustomerConnections(fetchCustomerconnectionsParams);
    const [connectionId, setConnectionId] = useState(null);

    useEffect(() => {
        if (connections && connections.length > 0) {
            setConnectionId(connections[0].id);
        }
    }, [connections]);

    const loading =
        loadingApplication || loadingConnections || activatingRecipe;

    const path = useMemo(() => `connections/${connectionId}`, [connectionId]);

    return (
        <Modal
            data-testid="workatoConnectionsModal"
            open
            title={t('Users.actions.setUpIntegration', {
                appName: displayName
            })}
            onClose={() => {
                onClose();
                setRecipeError(null);
                setOpenModal(false);
            }}
            paperProps={{
                minWidth: ['400px', '720px'],
                width: '100%'
            }}
        >
            <ModalBody sx={{ minHeight: recipeError ? '0px' : '600px' }}>
                {loading && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        sx={{ minHeight: '600px' }}
                    >
                        <CircularProgress />
                    </Box>
                )}
                {!loading &&
                    (!recipeError ? (
                        <WorkatoIFrame
                            path={path}
                            handleMessageFn={handleMessageFn}
                            clientId={clientId}
                        />
                    ) : (
                        <ConnectionError />
                    ))}
            </ModalBody>
            {recipeError && (
                <ModalFooter>
                    <Button
                        data-testid="closeModalBtn"
                        data-trackid="admin_workatoConnections_close"
                        onClick={() => setOpenModal(false)}
                        variant="outlined"
                        color="primary"
                        style={{ marginRight: '8px' }}
                    >
                        {t('Common.close')}
                    </Button>
                    <Button
                        data-testid="viewAppBtn"
                        data-trackid="admin_workatoConnections_reauthenticate"
                        variant="contained"
                        onClick={() => setRecipeError(null)}
                        color="primary"
                    >
                        {t('Common.re-authenticate')}
                    </Button>
                </ModalFooter>
            )}
        </Modal>
    );
};
